import React, { useEffect, useRef } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { Estate, Unit } from "../../types/estate";
import * as turf from "@turf/turf";
import {
    BESPOKE_MAPDATA_LEEDS,
    BESPOKE_MAPDATA_MK,
    BESPOKE_MAPDATA_PERSHORE,
    BESPOKE_MAPDATA_SHEFFIELD,
    MAPBOX_DATA,
    MAPBOX_DATA_TYPE,
} from "../../types/CONSTS";

interface MapboxProps {
    accessToken: string;
    initialViewState?: {
        longitude: number;
        latitude: number;
        zoom: number;
    };
    style?: string;
    className?: string;
    estate?: Estate;
}

const Mapbox: React.FC<MapboxProps> = ({
    accessToken,
    initialViewState = {
        longitude: -74.006,
        latitude: 40.7128,
        zoom: 10,
    },
    estate,
    style = "mapbox://styles/sirendesign/cmfccfsi3000701r9hs00hh65",
}) => {
    const mapContainer = useRef<HTMLDivElement>(null);
    const map = useRef<mapboxgl.Map | null>(null);
    const popup = useRef<mapboxgl.Popup | null>(null);
    const specialBuildingIds = [624406416, 624406415, 624406418];

    const animateHighlighting = () => {
        if (!map.current) return;
    };

    useEffect(() => {
        if (!mapContainer.current) return;

        mapboxgl.accessToken = accessToken;

        map.current = new mapboxgl.Map({
            container: mapContainer.current,
            style: style,
            center: [initialViewState.longitude, initialViewState.latitude],
            zoom: initialViewState.zoom,
            antialias: true,
        });

        // Delay camera animation to allow buildings to load first
        setTimeout(() => {
            map.current?.flyTo({
                zoom: initialViewState.zoom + 5,
                duration: 5000,
                pitch: 60,
                essential: true,
            });
        }, 1000); // 1 second delay

         
        map.current.on("load", () => {
            if (!map.current) return;

            const img = new Image(60, 60);
            img.onload = () => {
                if (map.current && !map.current.hasImage("logo-icon")) {
                    map.current.addImage("logo-icon", img);
                }
            };
            img.src = "/ui/logo.svg";
        });

        // Add building highlights once all layers are loaded
        const initializeHighlights = () => {
            if (!map.current || popup.current) return; // Already initialized (popup.current serves as flag)
            
            highlightBuildingGroupsWithLabelsAndHover(
                map.current,
                MAPBOX_DATA
            );
            highlightBuildingGroupsWithLabelsAndHoverBespoke(
                map.current,
                BESPOKE_MAPDATA_LEEDS,
                "leeds"
            );
            highlightBuildingGroupsWithLabelsAndHoverBespoke(
                map.current,
                BESPOKE_MAPDATA_MK,
                "mk"
            );
            highlightBuildingGroupsWithLabelsAndHoverBespoke(
                map.current,
                BESPOKE_MAPDATA_SHEFFIELD,
                "sheffield"
            );
            highlightBuildingGroupsWithLabelsAndHoverBespoke(
                map.current,
                BESPOKE_MAPDATA_PERSHORE,
                "pershore"
            );
            
            popup.current = new mapboxgl.Popup(); // Mark as initialized
        };

        // Wait for style to fully load with a delay to ensure all layers render
        map.current.on("load", () => {
            setTimeout(initializeHighlights, 3000); // Increased to 3s for slower layer rendering (especially Pershore)
        });

        // Also initialize on idle if style loads without explicit load event
        let idleInitCheck = 0;
        map.current.on("idle", () => {
            idleInitCheck++;
            if (idleInitCheck >= 3 && !popup.current) { // Wait for 3 idle events
                initializeHighlights();
            }
        });
        const navControl = new mapboxgl.NavigationControl();
        map.current.addControl(navControl, "bottom-right");

        const fullscreenControl = new mapboxgl.FullscreenControl();
        map.current.addControl(fullscreenControl, "bottom-right");

        const geolocateControl = new mapboxgl.GeolocateControl({
            positionOptions: { enableHighAccuracy: true },
            trackUserLocation: true,
        });
        map.current.addControl(geolocateControl, "bottom-right");

        const scaleControl = new mapboxgl.ScaleControl({
            maxWidth: 80,
            unit: "metric",
        });
        map.current.addControl(scaleControl, "bottom-right");

        const modelOrigin: [number, number] = [
            -1.503275273230998, 53.77366452443711,
        ];
        const modelAltitude = 1;
        const modelRotate = [Math.PI / 2, 0, 0];

        const modelAsMercatorCoordinate =
            mapboxgl.MercatorCoordinate.fromLngLat(modelOrigin, modelAltitude);

        const modelTransform = {
            translateX: modelAsMercatorCoordinate.x,
            translateY: modelAsMercatorCoordinate.y,
            translateZ: 0,
            rotateX: modelRotate[0],
            rotateY: modelRotate[1],
            rotateZ: modelRotate[2],
            scale:
                modelAsMercatorCoordinate.meterInMercatorCoordinateUnits() * 10,
        };

        function highlightBuildingGroupsWithLabelsAndHover(
            map: any,
            buildingData: MAPBOX_DATA_TYPE[]
        ) {
            // Pershore building IDs to exclude from popups
            const pershoreIds = [
                436510598, 436510604, 435598975, 436510614, 436510615, 436510616,
                435598979, 436510599, 436510600, 436510601, 436510617, 436510602,
                436510618, 436510603, 436510605, 436510606, 436510607, 436510608,
                436510609, 436510610, 436510611, 436510612, 436510613, 435915796,
                436352759, 436352764, 436352765, 436352758, 436352763, 436352757,
                436352766, 436352762, 436352760, 436352749, 435915850, 435915849,
                436352755, 436352754, 436352753, 436352751, 436352750, 436352752
            ];

            const allFeatures = map.queryRenderedFeatures({
                layers: ["raised"],
            });

            const highlightFeatures: any[] = [];
            const labelFeatures: any[] = [];

            buildingData.forEach((group, index) => {
                const groupIds = Array.isArray(group.id)
                    ? group.id
                    : [group.id];
                const groupFeatures = allFeatures.filter((f: any) =>
                    groupIds.includes(f.id)
                );

                if (groupFeatures.length === 0) return;

                groupFeatures.forEach((f: any) => {
                    f.properties.groupId = index;
                    f.properties.clickthrough = group.clickthrough || null;
                });

                highlightFeatures.push(...groupFeatures);
                const combined = turf.featureCollection(groupFeatures);
                const centroid = turf.centroid(combined);
                const bbox = turf.bbox(combined);
                const bboxCenter = turf.center(turf.bboxPolygon(bbox));
                const labelPosition = bboxCenter;

                labelFeatures.push({
                    type: "Feature",
                    geometry: labelPosition.geometry,
                    properties: {
                        title: `${group.unit}`,
                        height: 10,
                        hasClickthrough: !!group.clickthrough,
                    },
                });
            });

            const highlightGeoJSON = {
                type: "FeatureCollection",
                features: highlightFeatures,
            };
            if (map.getSource("highlighted-feature")) {
                map.getSource("highlighted-feature").setData(highlightGeoJSON);
            } else {
                map.addSource("highlighted-feature", {
                    type: "geojson",
                    data: highlightGeoJSON,
                });

                map.addLayer({
                    id: "highlighted-layer",
                    type: "fill-extrusion",
                    source: "highlighted-feature",
                    paint: {
                        "fill-extrusion-color": [
                            "case",
                            ["boolean", ["feature-state", "hover"], false],
                            "#ff6666",                                                        
                            "#D6F366",
                        ],
                        "fill-extrusion-height": 10,
                        "fill-extrusion-base": ["get", "min_height"],
                        "fill-extrusion-opacity": 1,
                    },
                });
            }

            // Create permanent popups for each unit
            buildingData.forEach((group, index) => {
                // Check if this unit has any Pershore building IDs
                const groupIds = Array.isArray(group.id) ? group.id : [group.id];
                const isPershoreUnit = groupIds.some(id => pershoreIds.includes(id));

                const groupFeatures = highlightFeatures.filter(
                    (f) => f.properties.groupId === index
                );
                if (groupFeatures.length === 0) return;

                const combined = turf.featureCollection(groupFeatures);
                const centroid = turf.centroid(combined);
                const coords = centroid.geometry.coordinates as [number, number];

                let popupContent;
                
                if (isPershoreUnit) {
                    // Simple text-only popup for Pershore units
                    popupContent = `
                        <style>
                            .unit-popup .mapboxgl-popup-content {
                                background: transparent !important;
                                padding: 0 !important;
                                box-shadow: none !important;
                                pointer-events: none !important;
                            }
                            .unit-popup .mapboxgl-popup-tip {
                                display: none !important;
                            }
                        </style>
                        <p style="margin: 0; color: #0B2F36;  font-size: 12px;">${group.unit}</p>
                    `;
                } else {
                    // Styled popup for non-Pershore units
                    popupContent = `
                        <style>
                            .unit-popup .mapboxgl-popup-content {
                                background: transparent !important;
                                padding: 0 !important;
                                box-shadow: none !important;
                                pointer-events: none !important;
                            }
                            .unit-popup .mapboxgl-popup-tip {
                                display: none !important;
                            }
                        </style>
                        <style>
                            .unit-popup .mapboxgl-popup-content {
                                background: transparent !important;
                                padding: 0 !important;
                                box-shadow: none !important;
                                pointer-events: none !important;
                            }
                            .unit-popup .mapboxgl-popup-tip {
                                display: none !important;
                            }
                        </style>
                        <div style="display: flex; align-items: center; gap: 8px; background: #0B2F36; padding: 8px 6px; border-radius: 6px;">
                            <img src="/ui/logo.svg" style="width: 16px; height: 16px;" />
                            <p style="margin: 0; color: #ffffff; font-weight: bold; font-size: 14px;">${group.unit}${group.clickthrough ? ' - Available' : ''}</p>
                        </div>
                    `;
                }

                new mapboxgl.Popup({
                    closeButton: false,
                    closeOnClick: false,
                    className: 'unit-popup',
                    
                })
                    .setLngLat(coords)
                    .setHTML(popupContent)
                    .addTo(map);
            });

            let hoveredGroupId: number | null = null;

            map.on("mousemove", "highlighted-layer", (e: any) => {
                if (!e.features || !e.features.length) return;

                const groupId = e.features[0].properties.groupId;
                if (hoveredGroupId === groupId) return;

                if (hoveredGroupId !== null) {
                    const prevFeatures = highlightFeatures.filter(
                        (f) => f.properties.groupId === hoveredGroupId
                    );
                    prevFeatures.forEach((f) =>
                        map.setFeatureState(
                            { source: "highlighted-feature", id: f.id },
                            { hover: false }
                        )
                    );
                }

                const currentFeatures = highlightFeatures.filter(
                    (f) => f.properties.groupId === groupId
                );
                currentFeatures.forEach((f) =>
                    map.setFeatureState(
                        { source: "highlighted-feature", id: f.id },
                        { hover: true }
                    )
                );

                hoveredGroupId = groupId;
            });

            map.on("mouseleave", "highlighted-layer", () => {
                if (hoveredGroupId === null) return;

                const prevFeatures = highlightFeatures.filter(
                    (f) => f.properties.groupId === hoveredGroupId
                );
                prevFeatures.forEach((f) =>
                    map.setFeatureState(
                        { source: "highlighted-feature", id: f.id },
                        { hover: false }
                    )
                );

                hoveredGroupId = null;
            });

            animateHighlighting();

            map.on("click", "highlighted-layer", (e: any) => {
                if (!e.features || !e.features.length) return;

                const clickthrough = e.features[0].properties.clickthrough;
                if (clickthrough) {
                    window.location.href = clickthrough;
                }
            });
        }

        function highlightBuildingGroupsWithLabelsAndHoverBespoke(
            map: any,
            buildingData: MAPBOX_DATA_TYPE[],
            bespokeName: string
        ) {
            const highlightSource = `highlighted-feature-${bespokeName}`;
            const highlightLayer = `highlighted-layer-${bespokeName}`;
            const labelSource = `highlighted-label-${bespokeName}`;
            const labelLayer = `highlighted-label-layer-${bespokeName}`;
            
            // For Pershore, check bespoke layer first, fallback to raised layer
            let allFeatures = map.queryRenderedFeatures({
                layers: [`space-industrial-${bespokeName}`],
            });
            
            if (allFeatures.length === 0 && bespokeName === "pershore") {
                allFeatures = map.queryRenderedFeatures({
                    layers: ["raised"],
                });
                console.log("Pershore: Using fallback raised layer, found", allFeatures.length, "features");
                
                // Log sample of available IDs for debugging
                if (allFeatures.length > 0) {
                    const sampleIds = allFeatures.slice(0, 5).map((f: any) => f.id);
                    console.log("Pershore: Sample of available raised layer IDs:", sampleIds);
                }
            }

            const highlightFeatures: any[] = [];
            const labelFeatures: any[] = [];

            buildingData.forEach((group, index) => {
                const groupIds = Array.isArray(group.id)
                    ? group.id.map(String)
                    : [String(group.id)];

                const groupFeatures = allFeatures.filter((f: any) => {
                    // For Pershore from raised layer, match by id directly
                    if (bespokeName === "pershore" && !f.properties.beskpoke_id) {
                        return groupIds.includes(String(f.id));
                    }
                    // For bespoke layers, match by beskpoke_id
                    return groupIds.includes(String(f.properties.beskpoke_id));
                });

                if (groupFeatures.length === 0) {
                    if (bespokeName === "pershore") {
                        console.log(`Pershore group ${index} (IDs: ${groupIds.join(", ")}): No features found`);
                    }
                    return;
                }

                groupFeatures.forEach((f: any) => {
                    f.properties.groupId = index;
                    f.properties.clickthrough = group.clickthrough || null;
                    f.properties.height = 10;
                });

                highlightFeatures.push(...groupFeatures);

                const combined = turf.featureCollection(groupFeatures);

                const centroid = turf.centroid(combined);
                const bbox = turf.bbox(combined);
                const bboxCenter = turf.center(turf.bboxPolygon(bbox));

                const labelPosition = bboxCenter;

                labelFeatures.push({
                    type: "Feature",
                    geometry: labelPosition.geometry,
                    properties: {
                        title: `${group.unit}`,
                        height: 10,
                        hasClickthrough: !!group.clickthrough,
                    },
                    id: `label-${bespokeName}-${index}`,
                });
            });

            const highlightGeoJSON = {
                type: "FeatureCollection",
                features: highlightFeatures.map((f: any) => ({
                    type: "Feature",
                    geometry: f.geometry,
                    properties: f.properties,
                    id: f.properties.beskpoke_id,
                })),
            };

            if (map.getSource(highlightSource)) {
                map.getSource(highlightSource).setData(highlightGeoJSON);
            } else {
                map.addSource(highlightSource, {
                    type: "geojson",
                    data: highlightGeoJSON,
                });

                map.addLayer({
                    id: highlightLayer,
                    type: "fill-extrusion",
                    source: highlightSource,
                    paint: {
                        "fill-extrusion-color": [
                            "case",
                            ["boolean", ["feature-state", "hover"], false],
                            "#ff6666",                                              
                            "#D6F366",
                        ],
                        "fill-extrusion-height": 10,
                        "fill-extrusion-base": 0,
                        "fill-extrusion-opacity": 1,
                    },
                });
            }

            // Create permanent popups for each unit
            buildingData.forEach((group, index) => {
                const groupFeatures = highlightFeatures.filter(
                    (f) => f.properties.groupId === index
                );
                if (groupFeatures.length === 0) return;

                const combined = turf.featureCollection(groupFeatures);
                const centroid = turf.centroid(combined);
                const coords = centroid.geometry.coordinates as [number, number];

                let popupContent;
                
                if (bespokeName === "pershore") {
                    // Simple text-only popup for bespoke Pershore units
                    popupContent = `
                        <style>
                            .unit-popup .mapboxgl-popup-content {
                                background: transparent !important;
                                padding: 0 !important;
                                box-shadow: none !important;
                                pointer-events: none !important;
                            }
                            .unit-popup .mapboxgl-popup-tip {
                                display: none !important;
                            }
                        </style>
                        <p style="margin: 0; color: #0B2F36; font-size: 12px;">${group.unit}</p>
                    `;
                } else {
                    // Styled popup for non-Pershore bespoke units
                    popupContent = `
                        <style>
                            .unit-popup .mapboxgl-popup-content {
                                background: transparent !important;
                                padding: 0 !important;
                                box-shadow: none !important;
                                pointer-events: none !important;
                            }
                            .unit-popup .mapboxgl-popup-tip {
                                display: none !important;
                            }
                        </style>
                        <div style="display: flex; align-items: center; gap: 10px; background: #0B2F36; padding: 10px 15px; border-radius: 6px;">
                            <img src="/ui/logo.svg" style="width: 24px; height: 24px;" />
                            <p style="margin: 0; color: #ffffff; font-weight: bold; font-size: 14px;">${group.unit}${group.clickthrough ? ' - Available' : ''}</p>
                        </div>
                    `;
                }

                new mapboxgl.Popup({
                    closeButton: false,
                    closeOnClick: false,
                    className: 'unit-popup',
                    offset: 10
                })
                    .setLngLat(coords)
                    .setHTML(popupContent)
                    .addTo(map);
            });

            let hoveredGroupId: number | null = null;
            const getFeatureId = (f: any) => f.properties.beskpoke_id || f.id;

            map.on("mousemove", highlightLayer, (e: any) => {
                if (!e.features || !e.features.length) return;

                const groupId = e.features[0].properties.groupId;
                if (hoveredGroupId === groupId) return;

                if (hoveredGroupId !== null) {
                    highlightFeatures
                        .filter((f) => f.properties.groupId === hoveredGroupId)
                        .forEach((f) =>
                            map.setFeatureState(
                                {
                                    source: highlightSource,
                                    id: getFeatureId(f),
                                },
                                { hover: false }
                            )
                        );
                }

                highlightFeatures
                    .filter((f) => f.properties.groupId === groupId)
                    .forEach((f) =>
                        map.setFeatureState(
                            {
                                source: highlightSource,
                                id: getFeatureId(f),
                            },
                            { hover: true }
                        )
                    );

                hoveredGroupId = groupId;
            });

            map.on("mouseleave", highlightLayer, () => {
                if (hoveredGroupId === null) return;

                highlightFeatures
                    .filter((f) => f.properties.groupId === hoveredGroupId)
                    .forEach((f) =>
                        map.setFeatureState(
                            {
                                source: highlightSource,
                                id: getFeatureId(f),
                            },
                            { hover: false }
                        )
                    );

                hoveredGroupId = null;
            });

            map.on("click", highlightLayer, (e: any) => {
                if (!e.features || !e.features.length) return;

                const clickthrough = e.features[0].properties.clickthrough;
                if (clickthrough) {
                    window.location.href = clickthrough;
                }
            });

            if (typeof animateHighlighting === "function") {
                animateHighlighting();
            }
        }

        return () => {
            try {
                if (map.current) {
                    map.current.remove();
                    map.current = null;
                }
                popup.current = null;
            } catch (e) {}
        };
    }, [accessToken, initialViewState, style]);

    return (
        <div ref={mapContainer} style={{ width: "100vw", height: "100%" }} />
    );
};

export default Mapbox;
